"use client";

import { ExpandIcon } from "@/assets/icons";
import { useGlobalMediaPlayer } from "@/hooks/useGlobalMediaPlayer";
import { Skeleton } from "antd";
import classNames from "classnames";
import {
  MediaControlBar,
  MediaController,
  MediaDurationDisplay,
  MediaLoadingIndicator,
  MediaPlayButton,
  MediaPosterImage,
  MediaTimeDisplay,
  MediaTimeRange,
} from "media-chrome/react";
import { observer } from "mobx-react-lite";
import React, {
  HTMLAttributes,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import PauseIcon from "./assets/pause.svg";
import PlayIcon from "./assets/play.svg";
import styles from "./index.module.css";

export interface IVideoPlayerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onTimeUpdate"> {
  src: string;
  loop?: boolean;
  poster?: string;
  muted?: boolean;
  autoPlay?: boolean;
  showControls?: boolean;
  showPlayButton?: boolean;
  showTimeDisplay?: boolean;
  enableHoverPlay?: boolean;
  disableClickPlay?: boolean;
  disableAutoHide?: boolean;
  onTimeUpdate?: (currentTime: number) => void;
  range?: { start: number; end: number };
  maxHeight?: string | number;
  stopOnUnmount?: boolean;
  playButtonStyle?: {
    container: string;
    icon: string;
  };
}

export interface IVideoPlayerRef {
  play: () => void;
  pause: () => void;
  getVideoElement: () => HTMLVideoElement | null;
}

const VideoPlayerBase = React.forwardRef<IVideoPlayerRef, IVideoPlayerProps>(
  (props, ref) => {
    const {
      src,
      className,
      style,
      playButtonStyle = {
        container: "",
        icon: "",
      },
      disableAutoHide = false,
      disableClickPlay = false,
      enableHoverPlay = false,
      onTimeUpdate,
      poster,
      autoPlay = false,
      loop = true,
      muted = false,
      range,
      showControls = true,
      showPlayButton = true,
      maxHeight,
      stopOnUnmount = true,
    } = props;
    const [isVideoEnabled, setIsVideoEnabled] = useState(
      !enableHoverPlay && !disableClickPlay
    );
    const hoverTimerRef = useRef<NodeJS.Timeout>(undefined);
    const playPromiseRef = useRef<Promise<void>>(undefined);
    const videoRef = useRef<HTMLVideoElement>(null);
    const { isPlaying, handlePlay, handlePause } =
      useGlobalMediaPlayer(videoRef);
    const [isLoaded, setIsLoaded] = useState(false);

    useImperativeHandle(ref, () => ({
      play: () => {
        videoRef.current?.play();
      },
      pause: () => {
        videoRef.current?.pause();
      },
      getVideoElement: () => videoRef.current,
    }));

    const videoProps: Record<string, unknown> = useMemo(
      () => ({
        ...(muted && { muted }),
        ...(autoPlay && { autoPlay }),
        ...(loop && { loop }),
        poster,
        preload: "metadata",
      }),
      [poster, muted, autoPlay, loop]
    );

    const onTimeUpdateRef = useRef(onTimeUpdate);
    onTimeUpdateRef.current = onTimeUpdate;

    useEffect(() => {
      if (!isVideoEnabled || !videoRef.current) {
        return;
      }

      const currentVideo = videoRef.current;

      currentVideo.addEventListener("play", handlePlay);
      currentVideo.addEventListener("pause", handlePause);

      let onTimeUpdateFunc: (ev: Event) => unknown = () => {};
      if (onTimeUpdateRef.current) {
        onTimeUpdateFunc = (event: Event) => {
          const target = event.currentTarget as HTMLVideoElement;
          if (target) {
            onTimeUpdateRef.current?.(target.currentTime);
            if (
              range?.start &&
              range?.end &&
              (target.currentTime < range.start ||
                target.currentTime > range.end)
            ) {
              target.currentTime = range.start;
            }
          }
        };
        currentVideo.addEventListener("timeupdate", onTimeUpdateFunc);
      }

      return () => {
        currentVideo.removeEventListener("play", handlePlay);
        currentVideo.removeEventListener("pause", handlePause);
        if (onTimeUpdateRef.current) {
          currentVideo.removeEventListener("timeupdate", onTimeUpdateFunc);
        }
      };
    }, [range?.start, range?.end, isVideoEnabled, handlePlay, handlePause]);

    useEffect(() => {
      if (!videoRef.current || !src) {
        return;
      }

      videoRef.current.load();
    }, [src]);

    // Stop playback on unmount if enabled
    useEffect(() => {
      return () => {
        if (!stopOnUnmount) return;
        const video = videoRef.current;
        // Run asynchronously to allow awaiting pending play promises
        void (async () => {
          try {
            // If a play() call is still pending, wait for it to settle to avoid race conditions
            if (playPromiseRef.current) {
              await playPromiseRef.current;
              playPromiseRef.current = undefined;
            }
            // Ensure global media player state is updated
            handlePause();
            // Clear any pending hover timers
            if (typeof window !== "undefined" && hoverTimerRef.current) {
              window.clearTimeout(hoverTimerRef.current);
            }
            if (video) {
              video.pause();
              video.currentTime = 0;
            }
          } catch (error) {
            console.error("Video cleanup error:", error);
          }
        })();
      };
    }, [stopOnUnmount]);

    const handleStartPlay = React.useCallback(() => {
      if (!isPlaying && !playPromiseRef.current) {
        setIsVideoEnabled(true);
        hoverTimerRef.current = setTimeout(() => {
          if (videoRef.current?.src) {
            playPromiseRef.current = videoRef.current
              .play()
              .catch((e) => {
                // Note: 部分情况下自动播放会被浏览器拦截或者意外移除，导致这里的 promise 一直处于 rejected 状态，导致这里的 if 条件一直处于 false 状态
                console.error("Video play error:", e);
              })
              .finally(() => {
                // 这里对 playPromiseRef 做重置处理避免影响后续的 mouseEnter 逻辑
                // 由于无论 play() 返回的 promise 是何状态都需要重置 playPromiseRef，因此这个操作放在 finally 里
                playPromiseRef.current = undefined;
              });
          }
        }, 500);
      }
    }, [isPlaying]);

    const handleStopPlay = React.useCallback(async () => {
      setIsVideoEnabled(false);
      handlePause();
      if (window && hoverTimerRef.current) {
        window?.clearTimeout(hoverTimerRef.current);
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
      if (playPromiseRef.current) {
        await playPromiseRef.current;
        playPromiseRef.current = undefined;
      }
    }, [handlePause]);

    const handleMouseEnter = React.useCallback(() => {
      if (enableHoverPlay) {
        handleStartPlay();
      }
    }, [enableHoverPlay, handleStartPlay]);

    const handleMouseLeave = React.useCallback(() => {
      if (enableHoverPlay) {
        handleStopPlay();
      }
    }, [enableHoverPlay, handleStopPlay]);

    const handleExpand = React.useCallback(() => {
      videoRef.current?.requestFullscreen();
    }, []);

    const videoChildren = useMemo(() => {
      return (
        <video
          onLoadedMetadata={() => {
            setIsLoaded(true);
          }}
          onError={(e) => {
            // TODO: 应该添加错误状态
            console.error("video load error:", e);
          }}
          suppressHydrationWarning
          playsInline
          ref={videoRef}
          slot="media"
          src={src}
          style={{ maxHeight }}
          {...videoProps}
        ></video>
      );
    }, [src, videoProps, maxHeight]);

    useEffect(() => {
      setIsLoaded(false);
    }, [src]);

    return (
      <div
        className={classNames(
          className,
          "overflow-hidden w-full relative",
          styles.wrapper
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {!isLoaded && (
          <div className={"overflow-hidden w-full h-full"}>
            <div className="w-full h-full flex items-center justify-center aspect-video">
              <Skeleton.Node
                active
                className="block! w-full! h-full! aspect-video!"
              />
            </div>
          </div>
        )}
        <MediaController
          style={style}
          autohide={disableAutoHide ? "-1" : "1"}
          className={classNames("bg-transparent w-full flex flex-col", {
            "invisible absolute inset-0 -z-10": !isLoaded,
            "z-20": isLoaded,
          })}
        >
          {poster && (
            <MediaPosterImage
              src={poster}
              slot="poster"
              className="object-cover"
            />
          )}
          <div
            slot="centered-chrome"
            className={
              playButtonStyle.container
                ? playButtonStyle.container
                : "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60px] h-[60px] play-button"
            }
          >
            {!enableHoverPlay && showPlayButton && (
              <MediaPlayButton className="bg-transparent absolute inset-0 z-10 overflow-hidden">
                <div className={styles.playButton} slot="play">
                  <PlayIcon
                    className={
                      playButtonStyle.icon ? playButtonStyle.icon : "w-9 h-9"
                    }
                  />
                </div>
                <div className={styles.playButton} slot="pause">
                  <PauseIcon
                    className={
                      playButtonStyle.icon ? playButtonStyle.icon : "w-9 h-9"
                    }
                  />
                </div>
              </MediaPlayButton>
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <MediaLoadingIndicator></MediaLoadingIndicator>
            </div>
          </div>
          {videoChildren}

          {showControls && (
            <div className="w-full flex items-center justify-end px-4 gap-3 bg-gradient-to-t from-[rgba(32,32,32,0.4)] to-transparent">
              <MediaControlBar
                className={classNames(styles.controlBar, "controller-bar")}
              >
                <MediaPlayButton className="bg-transparent mr-2">
                  <PlayIcon className="w-[24px] h-[24px]" slot="play" />
                  <PauseIcon className="w-[24px] h-[24px]" slot="pause" />
                </MediaPlayButton>
                <div className="flex items-center gap-2 flex-1">
                  <MediaTimeDisplay className="bg-transparent"></MediaTimeDisplay>
                  <MediaTimeRange className={styles.timeRange}>
                    <span slot="preview"></span>
                  </MediaTimeRange>
                  <MediaDurationDisplay />
                </div>
              </MediaControlBar>

              <ExpandIcon
                onClick={handleExpand}
                className="cursor-pointer text-white"
              />
            </div>
          )}
        </MediaController>
      </div>
    );
  }
);

VideoPlayerBase.displayName = "VideoPlayer";

const VideoPlayer = observer(VideoPlayerBase);
VideoPlayer.displayName = "VideoPlayer";

export { VideoPlayer };
